import React, { FC } from 'react';
import {
  RiCheckboxCircleFill,
  RiCloseCircleFill,
  RiAlertLine,
} from 'react-icons/ri';
import { IoHourglassOutline } from 'react-icons/io5';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { APP_HEX } from '@/constant/theme/colors.constant';

// ─── Status enum ─────────────────────────────────────────────────────────────
export type ProgressPillStatus = 'success' | 'inprogress' | 'failed';

// ─── Theme config per status ─────────────────────────────────────────────────
const STATUS_THEME: Record<
  ProgressPillStatus,
  { grad: string; glow: string; label: string; text: string }
> = {
  success: {
    grad: `linear-gradient(135deg, ${APP_HEX.emeraldSoft} 0%, ${APP_HEX.emeraldDark} 100%)`,
    glow: `${APP_HEX.success}40`,
    label: ORGANISM_PROPS.PROGRESS_PILL.DONE,
    text: 'text-emerald-600 dark:text-emerald-400',
  },
  inprogress: {
    grad: `linear-gradient(135deg, ${APP_HEX.amberSoft} 0%, ${APP_HEX.amberDark} 100%)`,
    glow: `${APP_HEX.warning}40`,
    label: ORGANISM_PROPS.PROGRESS_PILL.RUNNING,
    text: 'text-amber-600 dark:text-amber-400',
  },
  failed: {
    grad: `linear-gradient(135deg, ${APP_HEX.redSoft} 0%, ${APP_HEX.redDark} 100%)`,
    glow: `${APP_HEX.danger}40`,
    label: ORGANISM_PROPS.PROGRESS_PILL.FAILED,
    text: 'text-red-500 dark:text-red-400',
  },
};

const STATUS_ICON: Record<ProgressPillStatus, React.ElementType> = {
  success: RiCheckboxCircleFill,
  inprogress: IoHourglassOutline,
  failed: RiCloseCircleFill,
};

// ─── Props ────────────────────────────────────────────────────────────────────
export interface ProgressPillProps {
  /** Current progress 0–100 */
  percentage: number;
  /** Overall status */
  status: ProgressPillStatus;
  /**
   * Number of dot segments in the bar (default: 5).
   * Filled segments = round(percentage / 100 * segments).
   */
  segments?: number;
  /**
   * Override the label text.
   * Defaults to "Done" / "Running" / "Failed" based on status.
   */
  label?: string;
  /**
   * When true, renders a "Config Missing" warning badge instead of the progress UI.
   * Use this when the underlying config needed to calculate progress is absent.
   */
  configMissing?: boolean;
  /** Extra Tailwind classes on the root div */
  className?: string;
  /** Test ID for automated testing */
  testId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────
const ProgressPill: FC<ProgressPillProps> = ({
  percentage,
  status,
  segments = 5,
  label,
  configMissing = false,
  className = '',
}) => {
  if (configMissing) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/40 max-w-[160px]">
        <RiAlertLine size={11} className="text-amber-500 flex-shrink-0" />
        <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-400">
          {ORGANISM_PROPS.PROGRESS_PILL.CONFIG_MISSING}
        </span>
      </div>
    );
  }

  const theme = STATUS_THEME[status];
  const Icon = STATUS_ICON[status];
  const filledSegs = Math.round((percentage / 100) * segments);
  const displayLabel = label ?? theme.label;

  return (
    <div className={`flex items-center gap-2 min-w-[155px] ${className}`}>
      {/* Icon badge */}
      <div
        className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-white"
        style={{ background: theme.grad, boxShadow: `0 2px 8px ${theme.glow}` }}
      >
        <Icon size={12} />
      </div>

      {/* Label + segmented bar + percentage */}
      <div className="flex flex-col gap-[3px] flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className={`text-[10px] font-bold leading-none ${theme.text}`}>
            {displayLabel}
          </span>
          <span
            className={`text-[10px] font-mono font-bold tabular-nums leading-none ${theme.text}`}
          >
            {percentage}%
          </span>
        </div>

        {/* Segmented dots */}
        <div className="flex items-center gap-[3px]">
          {Array.from({ length: segments }).map((_, i) => (
            <div
              key={i}
              className="flex-1 h-1 rounded-full transition-all duration-300"
              style={
                i < filledSegs
                  ? {
                    background: theme.grad,
                    boxShadow: `0 0 4px ${theme.glow}`,
                  }
                  : { background: 'currentColor', opacity: 0.12 }
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgressPill;
